import {
  Link,
  useTheme,
} from '@mui/material';
import {
  getPartnerEnrollmentStatus,
  getHomeDepotEnrollmentStatus,
  hdSupplyPartnerId,
  homeDepotPartnerId,
  rinnaiPartnerId,
  winsupplyPartnerId,
  behrPartnerId,
  hdxPartnerId,
  novoPartnerId,
} from '../../../../common/utils/PartnerEnrollmentUtils';
import { useNavigationUtils } from '../../../../common/utils/useNavigationUtils';
import { useAppSelector } from '../../../../store/hooks';
import {
  selectAllActivePartners,
} from '../../../../store/slices/dashboardSlice';
import {
  PartnerEnrollmentStatus, PartnerCategory, PartnerSource,
} from '../../../../common/enums';
import {
  HOME_DEPOT_SETUP_OR_SKIP_ROUTE,
  PARTNERS_ROUTE,
  PARTNERS_ROUTE_HOME_DEPOT,
} from '../../../../routes/namedRoutes';
import { HD_ENROLL_MODAL_PARAM } from '../../../../common/homeDepotEnrollmentIntent';
import { useSolutionOrg } from '../../../../config/useSolutionOrg';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';
import { useCustomEventTracking } from '../../../../common/useCustomEventTracking';
import {
  getBasePartnerCode,
  getCanonicalPartnerName,
  HD_SUPPLY_NAME, PartnerName, THE_HOME_DEPOT_NAME, WINSUPPLY_NAME,
} from '../../../../common/constants';
import {
  MaxRebateInfo, Partner, PartnerListCategory, PartnerLocaleType,
} from '../../../../common/models/Partner';
import { toSlug } from '../../../../common/utils/urlUtils';
import {
  meetsPartnerAccess,
  shouldShowContactSales,
} from '../../../../common/utils/MembershipTierAccess';
import { withoutDuplicatePartners } from '../../../../common/utils/DuplicatePartners';
import { useGetCompanyPartnerRelation } from './useGetCompanyPartnerRelation';
import { usePartnerExternalEnrollmentForms } from './PartnerSpecificFormConfig';

export interface ManufacturerDistributor {
  partnerName: string,
  partnerId: string,
}

export interface MappedPartnerConfig {
  id: string,
  partnerName: PartnerName,
  partnerCode: string,
  partnerSource: PartnerSource,
  title: string,
  logo: () => JSX.Element,
  description: string,
  perks: string[],
  enrollment: PartnerEnrollmentStatus,
  dataTestId: string,
  showManageRewards: boolean,
  openPartnerSite: () => void,
  isNewPartner: boolean,
  isManufacturer: boolean,
  isPopularPartner: boolean,
  isLocked: boolean,
  // Locked because the partner needs Advanced/Alliance, which cannot be self-upgraded to:
  // the CTA becomes "Contact sales" instead of "Upgrade now" (SN-874).
  requiresSalesContact: boolean,
  tier: string | null,
  requiresDistributor: boolean,
  manufacturerDistributors: ManufacturerDistributor[] | null,
  partnerProfileLink: string,
  category: PartnerListCategory,
  positionInCategory: number | null,
  partnerLogoUrl: string,
  supportsExistingAccountFlow: boolean,
  redirectPath?: string,
  onNavigate?: () => void,
  hasExtraExistingAccQuestion: boolean,
  isProfileEnabled: boolean,
  hasRequiredForm: boolean,
  requiredFormUrl?: string,
  requiredFormSsoDestination?: string,
  categories: PartnerCategory[],
  maxRebate: MaxRebateInfo,
}

// Base manufacturer-distributor mapping. This is filtered at runtime to only
// include active partners, ensuring inactive partners are automatically excluded
// from enrollment flows without code changes (SN-794).
const baseManufacturerDistributors: {
  [key: string]: ManufacturerDistributor[] | undefined
} = {
  [rinnaiPartnerId]: [{
    partnerName: WINSUPPLY_NAME,
    partnerId: winsupplyPartnerId,
  }, {
    partnerName: HD_SUPPLY_NAME,
    partnerId: hdSupplyPartnerId,
  }],
  [novoPartnerId]: [{
    partnerName: WINSUPPLY_NAME,
    partnerId: winsupplyPartnerId,
  }, {
    partnerName: HD_SUPPLY_NAME,
    partnerId: hdSupplyPartnerId,
  }],
  [behrPartnerId]: [{
    partnerName: THE_HOME_DEPOT_NAME,
    partnerId: homeDepotPartnerId,
  }],
  [hdxPartnerId]: [{
    partnerName: THE_HOME_DEPOT_NAME,
    partnerId: homeDepotPartnerId,
  }],
};

export const mappedPartnerConfigCompareFn = (a: MappedPartnerConfig, b: MappedPartnerConfig) => {
  // Treat nulls as "push to back"
  if (a.positionInCategory === null && b.positionInCategory === null) return 0;
  if (a.positionInCategory === null) return 1;
  if (b.positionInCategory === null) return -1;
  return a.positionInCategory - b.positionInCategory;
};

export function usePartnerConfigList() {
  const theme = useTheme();
  const { trackHomeDepotEnrollmentStart } = useCustomEventTracking();
  const { isNativeAppExperience } = useSolutionOrg();
  const { openExternalLink } = useNavigationUtils();
  const getCompanyPartnerRelation = useGetCompanyPartnerRelation();
  const activePartners = useAppSelector(selectAllActivePartners);
  const externalEnrollmentForms = usePartnerExternalEnrollmentForms();
  const { company } = useAppSelector(selectMemberInformation);
  const subscriptionTier = company?.subscription?.subscriptionTier;

  // Both copies of a duplicated partner come back now that the list is cumulative, so the
  // one that does not match this member's rung is dropped before anything is derived from
  // the list - notably the manufacturer/distributor mappings below (SN-996).
  //
  // A copy the member is already enrolled with is kept regardless of their rung. This list
  // also feeds lookups by id (usePartnerProfile, PartnerOverviewSection, the Your Partners
  // tab), so dropping an enrolled copy would make that enrollment disappear and 404 its
  // profile page - which is exactly what happens to someone who enrolled on Edge and then
  // upgraded.
  const enrolledPartnerCodes = new Set(
    (activePartners ?? [])
      .filter((partner) => !!getCompanyPartnerRelation(partner.id))
      .map((partner) => partner.code),
  );
  const visiblePartners = withoutDuplicatePartners(
    activePartners,
    subscriptionTier,
    enrolledPartnerCodes,
  );

  type MergedPartner = Partner & { isLocked: boolean, requiresSalesContact: boolean };
  const mergedPartners: MergedPartner[] = (visiblePartners ?? []).map((partner) => {
    const isLocked = !meetsPartnerAccess(subscriptionTier, partner.membershipType);
    const requiresSalesContact = shouldShowContactSales(subscriptionTier, partner.membershipType);
    return { ...partner, isLocked: isLocked || requiresSalesContact, requiresSalesContact };
  });

  // Filter manufacturer-distributor mappings to only include active partners.
  // This ensures inactive partners (e.g., HD Supply) are automatically excluded
  // from enrollment flows without requiring code changes (SN-794).
  const activePartnerIds = new Set(mergedPartners.map((p) => p.id));
  const manufacturerDistributors = Object.entries(baseManufacturerDistributors)
    .reduce((acc, [manufacturerId, distributors]) => {
      const activeDistributors = distributors?.filter((d) => activePartnerIds.has(d.partnerId));
      if (activeDistributors && activeDistributors.length > 0) {
        acc[manufacturerId] = activeDistributors;
      }
      return acc;
    }, {} as typeof baseManufacturerDistributors);

  const partnerLogoStyles = {
    width: theme.spacing(8),
    height: 'auto',
    cursor: 'pointer',
    border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(1),
  };

  const enrollHomeDepotBadgeClicked = () => {
    trackHomeDepotEnrollmentStart();
  };

  const openExtUrl = (extUrl: string) => {
    openExternalLink(extUrl);
  };

  const partnerConfigList: MappedPartnerConfig[] = mergedPartners.map((activePartner) => {
    const { config } = activePartner;
    if (!config) {
      return null;
    }

    const { partnerConfigTranslations } = config;
    // using English translation for now
    const engTranslation = partnerConfigTranslations.find(
      (translation) => translation.locale === PartnerLocaleType.ENGLISH,
    );

    const baseCode = getBasePartnerCode(activePartner.code);
    const requiredFormUrl = externalEnrollmentForms[baseCode]?.externalEnrollmentForm;
    const requiredFormSsoDestination = externalEnrollmentForms[baseCode]?.ssoDestination;

    // Rename-stable name for deriving the test-id and profile-link slug: keying the
    // URL off the stable code (via the canonical name) keeps bookmarked/shared
    // /partners/<slug> links working after a partner is renamed in Retool. The
    // route resolver (usePartnerProfile) slugifies the same canonical name.
    const partnerSlugName = getCanonicalPartnerName(activePartner.code, activePartner.name);

    return ({
      id: activePartner.id,
      partnerName: activePartner.name as PartnerName,
      partnerCode: activePartner.code,
      partnerSource: activePartner.source,
      title: engTranslation?.title || '',
      logo: () => (<Link
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <img src={config.logoUrl as string}
          style={{
            ...partnerLogoStyles,
          }}
          alt={`${activePartner.name} logo`}
        />
      </Link>),
      description: engTranslation?.description || '',
      perks: engTranslation?.perks || [],
      enrollment: activePartner.id === homeDepotPartnerId ?
        getHomeDepotEnrollmentStatus(getCompanyPartnerRelation(activePartner.id)) :
        getPartnerEnrollmentStatus(getCompanyPartnerRelation(activePartner.id)),
      dataTestId: `partners-${toSlug(partnerSlugName)}`,
      openPartnerSite: () => openExtUrl(config.primarySiteUrl),
      ...(activePartner.id === homeDepotPartnerId ? {
        // Native app has its own full-page HD setup flow. On web, send the member to the
        // Home Depot partner profile page and have it auto-open the enrollment modal, so
        // "Add rewards" behaves the same whether clicked from the partners list or the
        // partner's own profile page (SN-924).
        redirectPath: isNativeAppExperience ?
          `${HOME_DEPOT_SETUP_OR_SKIP_ROUTE}?returnPath=${PARTNERS_ROUTE}` :
          `${PARTNERS_ROUTE_HOME_DEPOT}?${HD_ENROLL_MODAL_PARAM}=true`,
        onNavigate: enrollHomeDepotBadgeClicked,
      } : {}),
      showManageRewards: getPartnerEnrollmentStatus(
        getCompanyPartnerRelation(activePartner.id),
      ) === PartnerEnrollmentStatus.Completed,
      partnerProfileLink: `${PARTNERS_ROUTE}/${toSlug(partnerSlugName)}?tier=${
        activePartner.source === PartnerSource.Edge ? 'free' : 'paid'
      }`,
      category: config.category,
      positionInCategory: config.positionInCategory,
      partnerLogoUrl: config.logoUrl as string,
      supportsExistingAccountFlow: config.supportsExistingAccountFlow,
      hasExtraExistingAccQuestion: config.hasExtraExistingAccQuestion,
      isNewPartner: config.isNewPartner,
      isManufacturer: config.isManufacturer,
      isPopularPartner: config.isPopularPartner ?? false,
      isLocked: activePartner.isLocked,
      requiresSalesContact: activePartner.requiresSalesContact,
      tier: config.tier ?? null,
      // A partner requires a distributor if it has distributors but is NOT a manufacturer itself
      // Example: Behr (not a manufacturer) requires Home Depot (distributor)
      // Example: Rinnai (IS a manufacturer) also has distributors but uses supportsManufacturerFlow
      // TODO: move this to DB/Retool config.
      requiresDistributor: !config.isManufacturer &&
        (manufacturerDistributors[activePartner.id]?.length ?? 0) > 0,
      isProfileEnabled: config.isProfileEnabled ?? true,
      // TODO: hard coding this for now.
      // Think of migrating to backend when automating enrollment forms
      manufacturerDistributors: manufacturerDistributors[activePartner.id] ?? null,
      categories: (config.categories?.map((cat) => cat.key as PartnerCategory) || []),
      maxRebate: config.maxRebate ?? null,
      hasRequiredForm: !!requiredFormUrl || !!requiredFormSsoDestination,
      ...(requiredFormUrl ? { requiredFormUrl } : {}),
      ...(requiredFormSsoDestination ? { requiredFormSsoDestination } : {}),
    }) satisfies MappedPartnerConfig;
  }).filter((x): x is MappedPartnerConfig => x !== null);

  return partnerConfigList;
}
